// Pure content of the Excel export ("Exportar Excel"): which sheets, which rows, which cell formats.
// No xlsx import here on purpose, so the workbook content is unit-testable as plain arrays; the
// download itself lives in statsExport.js.
//
// Cells are numbers with an Excel number format (not pre-formatted text), so the owner can sort,
// sum and chart them. A value the back could not compute (null) is an empty cell, never a 0.

import { ENCLOSURE_LABELS, SPORT_LABELS } from "../../constants/courtDomain";
import { buildProductRows } from "./courtStatsProducts";
import { buildCourtRows, buildHeatGrid, channelShares, deriveMetrics } from "./courtStatsMetrics";

import { hours, indicatorRow, int, money, pct, rangeText, statsFileNameFor } from "./statsSheetsCommon";

// [label, formatter, metrics key] for the Resumen indicators, in reading order.
const INDICATORS = [
  ["Ocupación", pct, "occupancy"],
  ["Horas disponibles", hours, "availableHours"],
  ["Horas reservadas", hours, "bookedHours"],
  ["Horas sin vender", hours, "unsoldHours"],
  ["Reservas", int, "reservations"],
  ["Canceladas", int, "cancelled"],
  ["Ausencias (No Show)", int, "noShows"],
  ["Tasa de cancelación", pct, "cancellationRate"],
  ["Tasa de ausencias", pct, "noShowRate"],
  ["Ingresos cobrados", money, "revenue"],
  ["Cobrado online", money, "revenueOnline"],
  ["Cobrado en mostrador (ZocoPDV)", money, "revenuePos"],
  ["Devoluciones", money, "reversed"],
  ["Pendiente de cobro", money, "pending"],
  ["Sin cobrar por ausencias", money, "noShowUncollected"],
  ["Ingreso por hora disponible", money, "revenuePerHour"],
  ["Anticipación promedio (horas)", hours, "avgLeadHours"],
  ["Reservas hechas el mismo día", pct, "sameDayShare"],
];

// Flat metrics plus the numbers the shared derivation does not expose.
const exportMetrics = (info) => ({ ...deriveMetrics(info), reversed: info.totals.revenueCollected?.reversed ?? null });

const resumenSheet = ({ current, comparison, clubName, sportLabel, courtLabel }) => {
  const mine = exportMetrics(current);
  const other = comparison ? exportMetrics(comparison) : null;
  const channelsNow = channelShares(current.channels);
  const channelsThen = comparison ? channelShares(comparison.channels) : null;

  const withComparison = (label, format, now, then) => indicatorRow(label, format, now, then, Boolean(other));

  const rows = [
    ["Estadísticas de canchas"],
    ["Club", clubName],
    ["Período", rangeText(current)],
    ["Comparado con", comparison ? rangeText(comparison) : "Sin comparar"],
    ["Deporte", sportLabel],
    ["Cancha", courtLabel],
    [],
    other ? ["Indicador", "Este período", "Período de comparación"] : ["Indicador", "Este período"],
    ...INDICATORS.map(([label, format, key]) => withComparison(label, format, mine[key], other?.[key])),
  ];
  channelsNow.forEach((channel, i) => {
    rows.push(withComparison(`Reservas por ${channel.label}`, int, channel.reservations, channelsThen?.[i].reservations));
  });
  channelsNow.forEach((channel, i) => {
    rows.push(withComparison(`Ingresos por ${channel.label}`, money, channel.revenue, channelsThen?.[i].revenue));
  });
  return rows;
};

const diarioSheet = ({ current }) => [
  ["Fecha", "Horas disponibles", "Horas reservadas", "Ocupación", "Reservas", "Ingresos cobrados"],
  ...current.daily.map((day) => [
    day.date,
    hours(day.availableMinutes / 60),
    hours(day.bookedMinutes / 60),
    pct(day.availableMinutes > 0 ? day.bookedMinutes / day.availableMinutes : null),
    int(day.reservations),
    money(day.revenue),
  ]),
];

const horariosSheet = ({ current }) => {
  const grid = buildHeatGrid(current);
  return [
    ["Día", ...grid.hours.map((hour) => `${String(hour).padStart(2, "0")} h`)],
    ...grid.rows.map((row) => [row.label, ...row.cells.map((cell) => pct(cell.value))]),
  ];
};

const canchasSheet = ({ current }) => {
  const totals = exportMetrics(current);
  const rows = buildCourtRows(current.perCourt).map((row, i) => {
    const raw = current.perCourt[i];
    return [
      row.name,
      SPORT_LABELS[row.sport] || row.sport,
      ENCLOSURE_LABELS[row.enclosure] || row.enclosure || "",
      int(row.reservations),
      hours(row.bookedHours),
      hours(raw.availableMinutes / 60),
      pct(row.occupancy),
      money(row.revenue),
      money(row.revenuePerHour),
      int(raw.cancelled),
      pct(row.cancellationRate),
      int(raw.noShows),
      pct(row.noShowRate),
    ];
  });
  const sheet = [
    [
      "Cancha",
      "Deporte",
      "Cerramiento",
      "Reservas",
      "Horas reservadas",
      "Horas disponibles",
      "Ocupación",
      "Ingresos",
      "Ingreso por hora disponible",
      "Canceladas",
      "Tasa de cancelación",
      "Ausencias",
      "Tasa de ausencias",
    ],
    ...rows,
  ];
  if (rows.length > 1) {
    sheet.push([
      "Total del club",
      "",
      "",
      int(totals.reservations),
      hours(totals.bookedHours),
      hours(totals.availableHours),
      pct(totals.occupancy),
      money(totals.revenue),
      money(totals.revenuePerHour),
      int(totals.cancelled),
      pct(totals.cancellationRate),
      int(totals.noShows),
      pct(totals.noShowRate),
    ]);
  }
  return sheet;
};

// Every product of the period (no sport chip applies to the export), highest revenue first. An older
// back without `products` leaves just the header.
const tiposDeTurnoSheet = ({ current }) => [
  ["Turno", "Deporte", "Duración (min)", "Reservas", "Horas", "Facturado", "Promedio por reserva", "% del total"],
  ...buildProductRows(current.products).map((row) => [
    row.label,
    row.sportLabel,
    int(row.durationMinutes),
    int(row.reservations),
    hours(row.hours),
    money(row.revenue),
    money(row.average),
    pct(row.share),
  ]),
];

/**
 * Sheets of the workbook for the current filters.
 *
 * @param {{current: object, comparison: object|null, clubName: string, sportLabel: string,
 *   courtLabel: string}} input  `current`/`comparison` are stats responses; the labels describe the
 *   active filters ("Todos" / "Todas" when not narrowed).
 * @returns {Array<{name: string, rows: Array<Array>}>}  Resumen, Diario, Horarios, Canchas, Tipos de turno.
 */
export const buildStatsSheets = (input) => [
  { name: "Resumen", rows: resumenSheet(input) },
  { name: "Diario", rows: diarioSheet(input) },
  { name: "Horarios", rows: horariosSheet(input) },
  { name: "Canchas", rows: canchasSheet(input) },
  { name: "Tipos de turno", rows: tiposDeTurnoSheet(input) },
];

/** Download name: club and period in a filesystem-safe slug. */
export const statsFileName = ({ clubName, from, to }) =>
  statsFileNameFor({ prefix: "estadisticas-canchas", name: clubName, fallback: "club", from, to });
